import CONSTANTS from '@picsio/db/src/constants';

/**
 * Keyword ids that cascade detach will remove from the given assets.
 * Same rule as server planCascadeDetach: the requested keyword is the root,
 * and only descendants are removed (path starts with root path + delimiter).
 * Ancestors stay attached on purpose. Detaching one child is a count of 1,
 * so the confirm dialog stays closed. Missing parents are not an undercount.
 * A descendant is included only when that asset also has the requested keyword.
 * @param {string[]} keywordIds
 * @param {object[]} assets
 * @returns {string[]}
 */
export default function keywordsRemovedByCascade(keywordIds = [], assets = []) {
  const delimiter = CONSTANTS.NEW_PATH_DELIMITER;
  const requested = new Set(keywordIds.map((id) => String(id)));
  const removed = new Set();

  assets.forEach((asset) => {
    const attached = asset?.keywords || [];
    const roots = attached.filter((keyword) => (
      keyword?._id && keyword.path && requested.has(String(keyword._id))
    ));
    if (!roots.length) return;

    attached.forEach((keyword) => {
      if (!keyword?._id) return;
      /** Parent paths are shorter prefixes and must not match this check. */
      const matches = roots.some((root) => (
        String(keyword._id) === String(root._id)
        || (keyword.path && keyword.path.startsWith(`${root.path}${delimiter}`))
      ));
      if (matches) removed.add(String(keyword._id));
    });
  });

  return [...removed];
}

/**
 * Keyword ids the confirm dialog should count.
 * Loaded assets use the per-asset rule. When the catalog page does not hold
 * every selected asset (keyword filter, Select All), the Details chips are the
 * attachment set that is still on screen, paths included, and the cascade is
 * counted from them.
 *
 * The chips are a union across the selection, so in that fallback a descendant
 * can be counted even if it sits only on an asset without the requested
 * keyword. That overcount is intentional. The result only feeds the number in
 * the confirm dialog; the detach request always sends the requested ids and the
 * server applies the per-asset rule. Undercounting is the failure to avoid: it
 * hides the dialog or shows a smaller number while the server cascades fully.
 * Do not drop the fallback or restrict it to loaded assets.
 * @param {string[]} keywordIds
 * @param {{ selectedIds?: string[], items?: object[], aggregatedKeywords?: object[] }} selection
 * @returns {string[]}
 */
export function keywordsRemovedFromSelection(keywordIds = [], {
  selectedIds = [],
  items = [],
  aggregatedKeywords,
} = {}) {
  const selected = selectedIds.map((id) => String(id));
  const loaded = (items || []).filter((asset) => (
    asset?._id && selected.includes(String(asset._id))
  ));
  const fromLoaded = keywordsRemovedByCascade(keywordIds, loaded);
  const selectionLoaded = selected.length > 0 && loaded.length === selected.length;
  if (selectionLoaded || !Array.isArray(aggregatedKeywords)) return fromLoaded;

  const fromAggregated = keywordsRemovedByCascade(keywordIds, [{ keywords: aggregatedKeywords }]);
  return [...new Set([...fromLoaded, ...fromAggregated])];
}
